import { useScrollAnimation } from "@/hooks/use-scroll-animation";
import { useLanguage } from "@/hooks/use-language";
import lkabLogo from "@/assets/logos/customers/lkab.webp";
import scandiStandardLogo from "@/assets/logos/customers/scandi-standard.webp";
import sodraLogo from "@/assets/logos/customers/sodra.webp";
import quinyxLogo from "@/assets/logos/customers/quinyx.webp";
import novedoLogo from "@/assets/logos/customers/novedo.webp";
import brovikenLogo from "@/assets/logos/customers/broviken.webp";
import LatestNewsTeaser from "@/components/LatestNewsTeaser";

const Customers = () => {
  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation();
  const { ref: logosRef, isVisible: logosVisible } = useScrollAnimation();
  const { ref: cardsRef, isVisible: cardsVisible } = useScrollAnimation({ threshold: 0.05 });
  const { t } = useLanguage();

  const customers = [
    { name: "LKAB", logo: lkabLogo },
    { name: "Scandi Standard", logo: scandiStandardLogo },
    { name: "Södra", logo: sodraLogo },
    { name: "Quinyx", logo: quinyxLogo },
    { name: "Novedo", logo: novedoLogo },
    { name: "Broviken", logo: brovikenLogo },
  ];

  const targetGroups = [
    {
      title: t("Börsbolag", "Public companies"),
      description: t("Koncernredovisning enligt IFRS med full transparens och spårbarhet.", "Group accounting under IFRS with full transparency and traceability."),
      icon: "📈"
    },
    {
      title: t("PE-ägda grupper", "PE-owned groups"),
      description: t("Effektiv hantering av komplexa ägarstrukturer och rapportering.", "Efficient handling of complex ownership structures and reporting."),
      icon: "🏢"
    },
    {
      title: t("Serieförvärvare", "Serial acquirers"),
      description: t("Smidigt stöd vid frekventa förvärv, fusioner och omstruktureringar.", "Smooth support for frequent acquisitions, mergers and restructurings."),
      icon: "🔗"
    },
    {
      title: t("Kommuner & regioner", "Municipalities & regions"),
      description: t("Leasingredovisning enligt RKR R5 för offentlig sektor.", "Lease accounting under RKR R5 for the public sector."),
      icon: "🏛️"
    }
  ];

  return (
    <section id="customers" className="py-32 lg:py-44 bg-gradient-to-b from-background to-muted/50 relative overflow-hidden">
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-gold/5 rounded-full blur-[150px] -translate-y-1/2 translate-x-1/2" />
      
      <div className="container mx-auto px-6 lg:px-8 relative z-10">
        <div ref={headerRef} className={`max-w-3xl mx-auto text-center mb-16 transition-all duration-700 ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <span className="inline-block text-gold text-sm font-bold uppercase tracking-widest mb-4">
            {t("Våra kunder", "Our customers")}
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-display font-normal text-foreground mb-6">
            {t("Betrodda av", "Trusted by")}{" "}
            <span className="italic text-navy">{t("ledande organisationer", "leading organizations")}</span>
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed">
            {t(
              "Från börsnoterade bolag till kommuner och regioner – vi hjälper organisationer i alla storlekar.",
              "From listed companies to municipalities and regions – we support organizations of all sizes."
            )}
          </p>
        </div>

        <div ref={logosRef} className={`flex flex-wrap justify-center items-center gap-x-12 gap-y-6 mb-20 transition-all duration-700 delay-100 ${logosVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {customers.map((customer, index) => (
            <div key={customer.name} className="w-32 h-12 md:w-40 md:h-14 flex items-center justify-center opacity-60 hover:opacity-100 transition-all duration-300 cursor-default hover:scale-105" style={{ transitionDelay: `${index * 50}ms` }}>
              <img loading="lazy" decoding="async" src={customer.logo} alt={customer.name} width={160} height={56} className="max-h-full max-w-full object-contain grayscale hover:grayscale-0 transition-all duration-300" />
            </div>
          ))}
        </div>

        <div ref={cardsRef} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
          {targetGroups.map((group, index) => (
            <div key={group.title} className={`bg-white p-8 rounded-2xl shadow-card hover:shadow-card-hover hover:-translate-y-2 transition-all duration-500 text-center group border border-border/50 ${cardsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{ transitionDelay: `${200 + index * 100}ms` }}>
              <div className="text-4xl mb-4 group-hover:scale-110 transition-transform duration-300">{group.icon}</div>
              <h3 className="font-display text-xl text-foreground mb-3">{group.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{group.description}</p>
            </div>
          ))}
        </div>

        <div className="max-w-6xl mx-auto mt-24 lg:mt-32">
          <div className="flex items-center gap-4 justify-center mb-10 lg:mb-12">
            <span className="text-muted-foreground text-xs font-semibold uppercase tracking-widest">
              {t("Nyheter", "News")}
            </span>
          </div>
          <div className="rounded-3xl bg-card border border-border/50 shadow-card p-6 sm:p-8 lg:p-10">
            <LatestNewsTeaser />
          </div>
        </div>
      </div>
    </section>
  );
};

export default Customers;
